import _ from 'lodash';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { toaster, Notification } from 'rsuite';

import { CancellationsSVG, Card, CardBody, Table, HeaderTitle } from '../../../../components';
import { usersService, eventsService } from '../../../../services';
import { FilterQueryParams } from '../../../../helpers';
import { useCancellationsUIContext } from '../context/CancellationsUIContext';
import { UIHelpers } from '../../../../constants/pages/UIHelpers';
import cancellationsColumns from './CancellationsColumns';
import CancellationsFilters from './CancellationsFilters';
import CancellationsDialogView from '../dialog/CancellationDialogView';
import platforms from '../../../../constants/users/platforms';
import statuses from '../../../../constants/users/statuses';
import { setTextDatePicker } from '../../../../constants/date';

export default function CancellationsTable () {
    // sortKeyMap
    const customSortedKeyMap = { created_at_time: 'created_at' };
    const { sortedKeyMap, defaultSorted } = UIHelpers(customSortedKeyMap);
    const { initialFilter } = UIHelpers();
    // States

    // Context state
    const cancellationsUIContext = useCancellationsUIContext();
    const cancellationsUIProps = useMemo(() => {
        return {
            isLoading: cancellationsUIContext.isLoading,
            setIsLoading: cancellationsUIContext.setIsLoading,
            queryParams: cancellationsUIContext.queryParams,
            setQueryParams: cancellationsUIContext.setQueryParams
        };
    }, [cancellationsUIContext]);

    // List state
    const [cancellationsList, setCancellationsList] = useState();
    const [total, setTotal] = useState(0);

    // Dialogs state
    const [dialog, setDialog] = useState();

    const [dateFrom, setDateFrom] = useState();
    const [dateTo, setDateTo] = useState();
    const [searchText, setSearchText] = useState('');
    const [disableButton, setDisableButton] = useState(false);

    // Functions

    // Fetch inactive users first
    const fetchInactiveUsers = useCallback((page = 1) => {
        return usersService
            .find({
                // Filter only user for platform customer (Mobile applications (Android, IOS))
                filters: [
                    `id_user_status eq ${statuses.inactive.id}`,
                    `idPlatform eq ${platforms.consumer}`
                ].join(','),
                page
            })
            .then(fetchCancellations)
            .catch(e => {
                toaster.push(<Notification type="error" header="Error">Se produjo un error interno, te pedimos disculpas. Por favor, intentalo de nuevo más tarde.</Notification>);
                console.error('Error on fetch users', e);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchCancellations = (toSearch) => {
        cancellationsUIProps.setIsLoading(true);
        setTotal(toSearch.length);

        if (!toSearch.length) {
            toaster.push(<Notification type="warning" header="Advertencia">No hay usuarios dados de baja en este momento</Notification>);
            cancellationsUIProps.setIsLoading(false);
            return false;
        }

        const inactiveUsers = toSearch.map(user => user.id);

        // Prepare options (order, pages and filters)
        const filters = cancellationsUIProps.queryParams.filters || new FilterQueryParams();
        const { sortField, sortOrder } = cancellationsUIProps.queryParams;
        const options = {
            tiny: false,
            order: `${sortedKeyMap[sortField] || sortField}-${sortOrder.toUpperCase()}`,
            pageSize: cancellationsUIProps.queryParams.pageSize,
            page: cancellationsUIProps.queryParams.pageNumber
        };

        filters.in('metadata.id_user', inactiveUsers);

        filters.equal('id_event_type', 64000005);

        // Set filters on options.
        options.filters = filters.build();

        eventsService
            .find(options)
            .then(cancellations => {
                // change state cancellations list
                setCancellationsList(cancellations.map(cancellation => {
                    const client = _.find(toSearch, ['id', parseInt(cancellation.metadata.id_user)]);
                    cancellation.formattedName = client.formattedName;
                    cancellation.created_at_time = cancellation.created_at;
                    cancellation.email = client.person.emails[0];

                    return cancellation;
                }));

                // change state isLoading
                cancellationsUIProps.setIsLoading(false);
                return cancellations;
            })
            .catch(e => {
                console.error(e);
                toaster.push(<Notification type="error" header="Error">Se produjo un error interno, te pedimos disculpas. Por favor, intentalo de nuevo más tarde.</Notification>);
                // change state isLoading
                cancellationsUIProps.setIsLoading(false);
                return [];
            });

        return null;
    };

    const getCancellation = id => cancellationsList && cancellationsList.length ? _.find(cancellationsList, ['id', id]) : null;

    const reset = () => {
        cancellationsUIProps.setQueryParams(initialFilter);
        setDateFrom();
        setDateTo();
        setSearchText('');
        setTextDatePicker('rs-picker-toggle-value', 'Fecha de Alta Desde-Hasta');
        setTextDatePicker('rs-picker-toggle-placeholder', 'Fecha de Alta Desde-Hasta');
        setDisableButton(false);
    };

    // Events

    // Handle query params changes. On any param change, fetch comments.
    useEffect(() => {
        fetchInactiveUsers();
    }, [fetchInactiveUsers, cancellationsUIProps.queryParams]);

    const handleClickCancellationConfirm = id => {
        const cancellation = getCancellation(id);

        setDialog({
            type: 'view',
            cancellation
        });
    };

    // Main Component

    return (
        <>
            {(dialog && dialog.type === 'view') && (
                <CancellationsDialogView
                    open={true}
                    cancellation={dialog.cancellation}
                    onClose={() => setDialog(null)}
                />
            )}
            <Card>
                <div>
                    <HeaderTitle
                        title={'Bajas'}
                        total={total}
                        reset={reset}
                        icon={(<CancellationsSVG className='mr-1 mb-2'/>)}
                    />
                    <div>
                        <CancellationsFilters
                            isLoading={cancellationsUIProps.isLoading}
                            dateFrom={dateFrom}
                            setDateFrom={setDateFrom}
                            dateTo={dateTo}
                            setDateTo={setDateTo}
                            searchText={searchText}
                            setSearchText={setSearchText}
                            disableButton={disableButton}
                            setDisableButton={setDisableButton}
                        />
                    </div>
                </div>
                <CardBody>
                    <Table
                        data={cancellationsList}
                        columns={cancellationsColumns({ handleClickCancellationConfirm })}
                        sort={defaultSorted}
                        onChange={cancellationsUIProps.setQueryParams}
                        isLoading={cancellationsUIProps.isLoading}
                        pagination={{
                            sizePerPage: cancellationsUIProps.queryParams.pageSize,
                            page: cancellationsUIProps.queryParams.pageNumber
                        }}
                    />
                </CardBody>
            </Card>
        </>
    );
}
